import { AppMenuModel } from '@/models/AppViewModel';
import { Role } from '@/models/AccountEnums';
import {
  CONFIG_PARENT_PATHS,
  configLeafKey,
  getConfigSubrouteNavigationItems,
  isDefaultConfigSubroute,
} from '@/utils/navigationPatches';
import { isAdvisorRouteHidden } from '@/modules/advisor/utils/advisorNavigation';
import { normalizePath } from './appMenuFilter';

/**
 * Recursively flattens a menu tree into an array of normalized paths.
 * @param menu - The menu items to flatten
 * @returns Array of normalized paths from the menu
 */
function flattenMenuPaths(menu: AppMenuModel[]): string[] {
  const paths: string[] = [];

  const recurse = (items: AppMenuModel[]) => {
    for (const item of items) {
      if (item.Path) {
        paths.push(normalizePath(item.Path));
      }
      if (item.Items && item.Items.length > 0) {
        recurse(item.Items);
      }
    }
  };

  recurse(menu);
  return paths;
}

/**
 * Strips the role prefix from a URL path.
 * E.g., "/investor/Portfolio/Holdings" -> "/Portfolio/Holdings"
 *
 * @param path - The full path including role prefix
 * @param role - The current user role (e.g., "investor", "advisor")
 * @returns The path without the role prefix
 */
export function stripRolePrefix(
  path: string,
  role: string | null | undefined,
): string {
  if (!role) return path;

  const normalizedRole = role.toLowerCase();
  const normalizedPath = path.toLowerCase();

  // Handle paths like "/investor/..." or "/Investor/..."
  const rolePrefix = `/${normalizedRole}`;

  if (normalizedPath.startsWith(rolePrefix)) {
    // Return with preserved casing from original path
    const stripped = path.slice(rolePrefix.length);
    // Ensure it starts with / or return empty string for root
    return stripped || '/';
  }

  return path;
}

const ROLES_FOR_NORMALIZE: readonly ['firm', 'advisor'] = ['firm', 'advisor'];

/** Normalized paths for all Configuration sub-routes (Legal, Integrations, etc.) for both role variants. */
const NORMALIZED_CONFIG_SUBROUTE_PATHS: string[] = (() => {
  const routes = [
    ...getConfigSubrouteNavigationItems(Role.Firm),
    ...getConfigSubrouteNavigationItems(Role.Advisor),
  ]
    .map((item) => item.route)
    .filter((r): r is string => r != null);
  return [
    ...new Set(
      routes.flatMap((route) =>
        ROLES_FOR_NORMALIZE.map((role) =>
          normalizePath(stripRolePrefix(route, role)),
        ),
      ),
    ),
  ].filter(Boolean);
})();

/**
 * Validates if a URL path is permitted based on the user's menu.
 * Mirrors the legacy `permissions.validateByUrl` logic.
 *
 * @param url - The URL path to validate (without role prefix)
 * @param menu - The user's permitted menu items from UserInfo.Menu
 * @returns true if the URL is permitted, false otherwise
 *
 * @example
 * ```ts
 * const menu = userInfo.Menu;
 * const isAllowed = validateByUrl("/Portfolio/Holdings", menu);
 * ```
 */
export function validateByUrl(
  url: string,
  menu: AppMenuModel[] | undefined,
): boolean {
  if (!menu?.length) return true;

  const normalizedUrl = normalizePath(url);
  if (!normalizedUrl || normalizedUrl === '/') return true;

  let permittedPaths = flattenMenuPaths(menu);
  if (CONFIG_PARENT_PATHS.some((p) => permittedPaths.includes(p))) {
    // Mirror the applyMenuPatches Configuration gating: when the backend
    // enumerates children under the Configuration/Setup parent, only the
    // sub-routes it sent are permitted (firms can disable individual
    // sub-pages). A bare parent grants the default sub-route list.
    const sentLeafKeys = new Set(
      permittedPaths
        .filter((p) =>
          CONFIG_PARENT_PATHS.some((parent) => p.startsWith(`${parent}/`)),
        )
        .map((p) => configLeafKey(p))
        .filter(Boolean),
    );
    const injected =
      sentLeafKeys.size === 0
        ? NORMALIZED_CONFIG_SUBROUTE_PATHS.filter(isDefaultConfigSubroute)
        : NORMALIZED_CONFIG_SUBROUTE_PATHS.filter((p) =>
            sentLeafKeys.has(configLeafKey(p)),
          );
    permittedPaths = [...permittedPaths, ...injected];
  }

  if (permittedPaths.includes(normalizedUrl)) return true;

  // Check parent path for nested routes (legacy allows 3-segment match)
  // E.g., /Portfolio/Accounts/:accountNumber should match if /Portfolio/Accounts is in menu
  const segments = normalizedUrl.split('/').filter(Boolean);

  if (segments.length > 2) {
    // Try progressively shorter parent paths. Legacy floor: an ordinary
    // single-segment grant (e.g. a `/portfolio` section parent) is NOT a
    // wildcard for everything beneath it.
    for (let i = segments.length - 1; i >= 2; i--) {
      const parentPath = normalizePath('/' + segments.slice(0, i).join('/'));
      if (permittedPaths.includes(parentPath)) return true;
    }
  }

  return false;
}

/**
 * Effective route permission for NON-ADVISOR roles (plus the advisor
 * fail-closed fallback while the menu is missing). Advisor permissions
 * derive from the centralized nav artifact instead — see
 * `canAccessAdvisorRoute` / `selectAdvisorAllowedRoutes` in
 * `@/modules/advisor/selectors/advisorNavigation` — so what the sidebar
 * renders and what the guard allows come from the same object.
 * `useCanNavigateTo` (shared by `PagePermissionGuard`) does the branching.
 *
 * Composes, in order:
 * 1. Client-side hide policy — advisor-hidden routes denied (defense in
 *    depth for the fallback path); the Terms configuration page is denied
 *    for every non-Firm role (matching `FirmOnlyGuard` on the route).
 * 2. Fail closed on a missing/empty menu — unlike `validateByUrl`'s legacy
 *    fail-open, only the dashboard (and the role index) stays reachable.
 * 3. `validateByUrl` menu validation on the role-stripped path.
 *
 * `pathname` is the full app path (role prefix included), as from
 * `location.pathname`.
 */
export function canAccessRoute(
  pathname: string,
  role: Role | string | null | undefined,
  menu: AppMenuModel[] | undefined,
  grantedHiddenPaths: readonly string[] = [],
): boolean {
  if (role === Role.Advisor && isAdvisorRouteHidden(pathname)) {
    return false;
  }

  const pathWithoutRole = stripRolePrefix(pathname, role);

  // Terms configuration is firm-only; keep links and guard aligned with
  // FirmOnlyGuard for every role.
  if (
    role !== Role.Firm &&
    normalizePath(pathWithoutRole) === '/configuration/terms'
  ) {
    return false;
  }

  // Routes hidden from the sidebar (DEPRECATED_ROUTES) but reachable from a
  // page action: allowed only when the backend menu granted them to this user.
  if (grantedHiddenPaths.includes(normalizePath(pathWithoutRole))) {
    return true;
  }

  if (!menu || menu.length === 0) {
    const normalized = normalizePath(pathWithoutRole);
    // normalizePath('/') === '' — the role index renders the dashboard.
    return normalized === '/dashboard' || normalized === '';
  }

  return validateByUrl(pathWithoutRole, menu);
}
